import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  CircularProgress,
  MenuItem,
  Modal,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import CloseIcon from "@mui/icons-material/Close";
import IconButton from "@mui/material/IconButton";
import { toISODate, toLocalISOString } from "../../../../shared/utils/dateFormat";
import { formatPhoneNumber, onlyDigitsKeyDown } from "../../../../shared/utils/phoneFormat";

import { getSucursales } from "../api/empleadosApi";
import { getAreas } from "../api/areasApi";
import { getRoles } from "../api/rolesApi";

// ─── Opciones para Selects (values = enums del backend) ───
const MARITAL_OPTIONS = [
  { value: "", label: "— Sin especificar —" },
  { value: "SINGLE", label: "Soltero/a" },
  { value: "MARRIED", label: "Casado/a" },
  { value: "DIVORCED", label: "Divorciado/a" },
  { value: "WIDOWED", label: "Viudo/a" },
  { value: "UNION", label: "Unión de hecho" },
  { value: "OTHER", label: "Otro" },
];

const EDUCATION_OPTIONS = [
  { value: "", label: "— Sin especificar —" },
  { value: "NONE", label: "Sin estudios" },
  { value: "PRIMARY", label: "Primario" },
  { value: "SECONDARY", label: "Secundario" },
  { value: "TERTIARY", label: "Terciario" },
  { value: "UNIVERSITY", label: "Universitario" },
  { value: "POSTGRAD", label: "Posgrado" },
  { value: "OTHER", label: "Otro" },
];

const STATUS_OPTIONS = [
  { value: "ACTIVE", label: "Activo" },
  { value: "INACTIVE", label: "Inactivo" },
];

const CONTRACT_TYPE_OPTIONS = [
  { value: "", label: "— Seleccionar tipo —" },
  { value: "FORMAL", label: "Formal" },
  { value: "INFORMAL", label: "Informal" },
];

const SALARY_MODE_OPTIONS = [
  { value: "", label: "— Sin especificar —" },
  { value: "MENSUAL", label: "Mensual (sueldo base)" },
  { value: "HORA", label: "Por hora (tarifa horaria)" },
];

// ─── Helpers ───
function buildInitialForm(e) {
  if (!e) return {};

  const activeBranchIds = (e.employeeBranches || [])
    .filter((row) => !row.deletedAt && (!row.toAt || new Date(row.toAt) >= new Date()))
    .map((row) => String(row.branchId ?? row.branch?.id))
    .filter(Boolean)
    .sort();

  const activeAreaIds = (e.employeeAreas || [])
    .filter((row) => !row.deletedAt)
    .map((row) => String(row.areaId ?? row.area?.id))
    .filter(Boolean)
    .sort();

  const activeRoleIds = (e.employeeRoles || [])
    .filter((row) => !row.deletedAt)
    .map((row) => String(row.roleId ?? row.role?.id))
    .filter(Boolean)
    .sort();

  return {
    full_name: e.fullName ?? "",
    email: e.user?.email ?? e.email ?? "",
    dni: e.dni ?? "",
    cuit: e.cuit ?? "",
    birth_date: toISODate(e.birthDate),
    marital_status: e.maritalStatus ?? "",
    children_count: e.childrenCount ?? 0,
    education_level: e.educationLevel ?? "",
    health: e.health ?? "",
    phone_number: e.phoneNumber ?? "",
    nationality: e.nationality ?? "",
    province: e.province ?? "",
    locality: e.locality ?? "",
    neighborhood: e.neighborhood ?? "",
    address: e.address ?? "",
    postal_code: e.postalCode ?? "",
    branch_ids: activeBranchIds,
    area_ids: activeAreaIds,
    role_ids: activeRoleIds,
    contract_name: e.contractName ?? "",
    hire_date: toISODate(e.hireDate),
    clothing: e.clothing ?? "",
    status: e.status ?? "ACTIVE",
    contract_end_date: toISODate(e.contractEndDate),
    salary: e.salaryDecimal ?? 0,
    // ─── Perfil de liquidación (viene como relación `payrollProfile` o anidado) ───
    salary_mode: e.payrollProfile?.salaryMode ?? "",
    base_salary: e.payrollProfile?.baseSalary ?? "",
    hourly_rate: e.payrollProfile?.hourlyRate ?? "",
    payroll_cvu: e.payrollProfile?.cvu ?? "",
    payroll_bank: e.payrollProfile?.bank ?? "",
    payroll_account_alias: e.payrollProfile?.accountAlias ?? "",
    payroll_notes: e.payrollProfile?.notes ?? "",
  };
}

// ─── Shared field styles ───
const fieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "12px",
    bgcolor: "rgba(255,255,255,0.85)",
    fontSize: "14px",
    fontFamily: '"Open Sans", Helvetica, Arial, sans-serif',
    "& fieldset": {
      borderColor: "rgba(40, 80, 160, 0.3)",
    },
    "&:hover fieldset": {
      borderColor: "rgba(40, 80, 160, 0.6)",
    },
    "&.Mui-focused fieldset": {
      borderColor: "rgba(14, 50, 149, 1)",
    },
  },
  "& .MuiInputLabel-root": {
    fontFamily: '"Open Sans", Helvetica, Arial, sans-serif',
    fontSize: "13px",
    fontWeight: 600,
    color: "rgba(2, 8, 23, 0.7)",
  },
};

// ─── EditableField ───
function EditableField({
  label,
  name,
  value,
  onChange,
  type = "text",
  select = false,
  options = [],
  multiline = false,
  rows,
  multiple = false,
  placeholderMultiple = "Seleccionar",
  numericOnly = false,
  formatDisplay = null,
  max = undefined,
  error = false,
  helperText = "",
}) {
  return (
    <TextField
      label={label}
      name={name}
      value={
        select
          ? (value ?? (multiple ? [] : ""))
          : (formatDisplay ? formatDisplay(value ?? "") : (value ?? ""))
      }
      onChange={onChange}
      onKeyDown={numericOnly ? onlyDigitsKeyDown : undefined}
      type={type}
      select={select}
      multiline={multiline}
      rows={rows}
      fullWidth
      size="small"
      variant="outlined"
      error={error}
      helperText={helperText || undefined}
      inputProps={{ max }}
      InputLabelProps={type === "date" ? { shrink: true } : undefined}
      SelectProps={
        select
          ? {
            multiple,
            renderValue: multiple
              ? (selected) => {
                if (!selected || selected.length === 0) {
                  return `— ${placeholderMultiple} —`;
                }
                return options
                  .filter((opt) => selected.includes(opt.value))
                  .map((opt) => opt.label)
                  .join(", ");
              }
              : undefined,
          }
          : undefined
      }
      sx={fieldSx}
    >
      {select &&
        options.map((opt) => (
          <MenuItem key={opt.value} value={opt.value}>
            {opt.label}
          </MenuItem>
        ))}
    </TextField>
  );
}
// ─── SectionAccordion ───
function SectionAccordion({ title, children, defaultExpanded = false }) {
  return (
    <Accordion
      defaultExpanded={defaultExpanded}
      disableGutters
      elevation={0}
      sx={{
        bgcolor: "transparent",
        "&:before": { display: "none" },
        borderBottom: "1px solid rgba(142, 142, 159, 0.3)",
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{ px: 0, minHeight: 48 }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <EditIcon sx={{ fontSize: 18, color: "rgba(40, 80, 160, 0.6)" }} />
          <Typography
            sx={{
              fontFamily: '"Open Sans", Helvetica, Arial, sans-serif',
              fontSize: "16px",
              fontWeight: 600,
              color: "rgba(40, 80, 160, 1)",
            }}
          >
            {title}
          </Typography>
        </Stack>
      </AccordionSummary>
      <AccordionDetails sx={{ px: 0, pt: 0, pb: 2 }}>
        {children}
      </AccordionDetails>
    </Accordion>
  );
}

// ─── Empty form template for create mode ───
function buildEmptyForm() {
  return {
    full_name: "",
    email: "",
    dni: "",
    cuit: "",
    birth_date: "",
    marital_status: "",
    children_count: 0,
    education_level: "",
    health: "",
    phone_number: "",
    nationality: "",
    province: "",
    locality: "",
    neighborhood: "",
    address: "",
    postal_code: "",
    branch_ids: [],
    area_ids: [],
    role_ids: [],
    contract_name: "",
    hire_date: "",
    clothing: "",
    status: "ACTIVE",
    contract_end_date: "",
    salary: 0,
    // ─── Perfil de liquidación ───
    salary_mode: "",
    base_salary: "",
    hourly_rate: "",
    payroll_cvu: "",
    payroll_bank: "",
    payroll_account_alias: "",
    payroll_notes: "",
  };
}

function unwrapList(res) {
  if (Array.isArray(res)) return res;
  if (res?.data && Array.isArray(res.data)) return res.data;
  if (res?.items && Array.isArray(res.items)) return res.items;
  return [];
}

// ─── Main Component ───
export default function EmpleadoFichaAccordion({
  open,
  onClose,
  employee,
  onSave,
  saving = false,
  onDescargarLegajo,
  downloadingLegajo = false,
  mode = "edit", // "edit" | "create"
  onCreate, // async (formData) => void
}) {
  const isCreate = mode === "create";

  const [form, setForm] = useState({});
  const [saveMsg, setSaveMsg] = useState("");
  const [saveMsgType, setSaveMsgType] = useState("success"); // "success" | "error"
  const [creating, setCreating] = useState(false);

  // ─── Catálogos dinámicos (sucursales, áreas, roles) ───
  const [branches, setBranches] = useState([]);
  const [areas, setAreas] = useState([]);
  const [roles, setRoles] = useState([]);

  const fetchCatalogs = useCallback(async () => {
    try {
      const [branchRes, areaRes, roleRes] = await Promise.all([
        getSucursales().catch(() => []),
        getAreas().catch(() => []),
        getRoles().catch(() => []),
      ]);
      setBranches(unwrapList(branchRes));
      setAreas(unwrapList(areaRes));
      setRoles(unwrapList(roleRes));
    } catch {
      // silencioso
    }
  }, []);

  useEffect(() => {
    if (open) fetchCatalogs();
  }, [open, fetchCatalogs]);

  const branchOptionsMulti = useMemo(
    () =>
      branches.map((b) => ({
        value: String(b.id),
        label: b.name || b.nombre || b.nombre_sucursal || `Sucursal ${b.id}`,
      })),
    [branches]
  );

  const areaOptionsMulti = useMemo(
    () =>
      areas.map((a) => ({
        value: String(a.id),
        label: a.name || a.nombre || `Área ${a.id}`,
      })),
    [areas]
  );

  const roleOptionsMulti = useMemo(
    () =>
      roles.map((r) => ({
        value: String(r.id),
        label: r.name || r.nombre || `Rol ${r.id}`,
      })),
    [roles]
  );

  // Reset form when employee changes (edit) or when modal opens (create)
  useEffect(() => {
    if (isCreate && open) {
      setForm(buildEmptyForm());
      setSaveMsg("");
      setCreating(false);
    } else if (employee) {
      setForm(buildInitialForm(employee));
      // No limpiar saveMsg acá → si acabamos de guardar, el mensaje debe permanecer visible
    }
  }, [employee, isCreate, open]);

  // Auto-limpiar el mensaje de éxito después de 3 segundos
  useEffect(() => {
    if (!saveMsg) return;
    const timer = setTimeout(() => setSaveMsg(""), 3000);
    return () => clearTimeout(timer);
  }, [saveMsg]);

  function handleChange(e) {
    const { name, value } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  }

  // Edit mode: true if any field differs from original
  const hasChanges = useMemo(() => {
    if (isCreate) return false; // not used in create
    if (!employee) return false;
    const initial = buildInitialForm(employee);
    return Object.keys(initial).some(
      (k) => String(form[k] ?? "") !== String(initial[k] ?? ""),
    );
  }, [form, employee, isCreate]);

  // Create mode: require nombre, DNI, email, sucursal, area, rol
  const canCreate = useMemo(() => {
    if (!isCreate) return false;
    return (
      (form.full_name ?? "").trim().length > 0 &&
      (form.dni ?? "").trim().length > 0 &&
      (form.email ?? "").trim().length > 0 &&
      Array.isArray(form.branch_ids) &&
      form.branch_ids.length > 0 &&
      Array.isArray(form.area_ids) &&
      form.area_ids.length > 0 &&
      Array.isArray(form.role_ids) &&
      form.role_ids.length > 0
    );
  }, [form, isCreate]);

  // Fecha máxima permitida para fecha de nacimiento (hoy en formato YYYY-MM-DD)
  const todayISO = useMemo(() => new Date().toISOString().split("T")[0], []);

  // Error de fecha de nacimiento: valor ingresado > hoy
  const birthDateError = useMemo(() => {
    const v = form.birth_date;
    if (!v) return false;
    const year = v.split("-")[0] ?? "";
    if (year.length < 4) return false;
    return v > todayISO;
  }, [form.birth_date, todayISO]);


  // Convierte los campos de fecha "YYYY-MM-DD" a ISO con timezone local
  // para que el backend interprete el día correcto (evita desfase UTC)
  function serializeDates(data) {
    const DATE_KEYS = ["birth_date", "hire_date", "contract_end_date"];
    const result = { ...data };
    for (const key of DATE_KEYS) {
      if (result[key]) result[key] = toLocalISOString(result[key]);
    }
    return result;
  }

  async function handleSave() {
    if (!onSave || !employee?.id) return;
    setSaveMsg("");
    try {
      const payload = serializeDates({
        ...form,
        salary: form.base_salary || 0,
      });
      await onSave(employee.id, payload);
      setSaveMsgType("success");
      setSaveMsg("Cambios guardados correctamente");
    } catch (err) {
      setSaveMsgType("error");
      setSaveMsg(err?.message || "Error al guardar");
    }
  }

  async function handleCreate() {
    if (!onCreate) return;
    setSaveMsg("");
    setCreating(true);
    try {
      const formData = serializeDates({
        ...form,
        branchIds: (form.branch_ids || []).map(Number),
        areaIds: (form.area_ids || []).map(Number),
        roleIds: (form.role_ids || []).map(Number),
        salary: form.base_salary || 0,
        // Perfil de liquidación
        salaryMode: form.salary_mode || undefined,
        baseSalary: form.base_salary || undefined,
        hourlyRate: form.hourly_rate || undefined,
        cvu: form.payroll_cvu || undefined,
        bank: form.payroll_bank || undefined,
        accountAlias: form.payroll_account_alias || undefined,
        payrollNotes: form.payroll_notes || undefined,
      });

      await onCreate(formData);
      setSaveMsgType("success");
      setSaveMsg("Empleado creado correctamente");
      setTimeout(() => onClose?.(), 600);
    } catch (err) {
      setSaveMsgType("error");
      setSaveMsg(err?.message || "Error al crear empleado");
    } finally {
      setCreating(false);
    }
  }

  // In edit mode, don't render if no employee
  if (!isCreate && !employee) return null;

  const fullName = isCreate
    ? form.full_name || "Nuevo Empleado"
    : form.full_name || employee?.full_name || employee?.nombre || "Empleado";

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="empleado-ficha-accordion"
    >
      <Stack
        spacing={2}
        sx={{
          width: "100vw",
          height: "100vh",
          overflowY: "auto",
          padding: 4,
          backgroundColor: "rgba(242, 243, 249, 1)",
          outline: "none",
          position: "relative",
        }}
      >
        <IconButton
          onClick={onClose}
          sx={{
            position: "absolute",
            top: 16,
            right: 20,
            color: "rgba(2, 8, 23, 0.7)",
          }}
        >
          <CloseIcon />
        </IconButton>
        {/* Título */}
        <Stack spacing={0.5} sx={{ alignItems: "center" }}>
          <Typography
            id="empleado-ficha-accordion"
            sx={{
              fontFamily: '"Open Sans", Helvetica, Arial, sans-serif',
              fontSize: "18px",
              fontWeight: 600,
              color: "rgba(2, 8, 23, 1)",
            }}
          >
            {isCreate ? "Agregar Personal" : `Ficha del Empleado - ${fullName}`}
          </Typography>
          <Typography
            sx={{
              fontSize: "12px",
              fontWeight: 400,
              color: "rgba(80, 80, 100, 0.75)",
              fontStyle: "italic",
            }}
          >
            {isCreate
              ? "Completá los datos del nuevo empleado"
              : "Hacé click en cualquier campo para editarlo"}
          </Typography>
        </Stack>

        {/* Accordions */}
        <Box sx={{ px: 1 }}>
          {/* ─── Datos Personales ─── */}
          <SectionAccordion title="Datos Personales" defaultExpanded>
            <Stack spacing={1.5}>
              <Stack direction="row" spacing={1.5}>
                <EditableField
                  label="Nombre y apellido"
                  name="full_name"
                  value={form.full_name}
                  onChange={handleChange}
                />
                <EditableField
                  label="Email"
                  name="email"
                  value={form.email}
                  onChange={handleChange}
                  type="email"
                />
              </Stack>
              <Stack direction="row" spacing={1.5}>
                <EditableField
                  label="DNI"
                  name="dni"
                  value={form.dni}
                  onChange={handleChange}
                />
                <EditableField
                  label="CUIT"
                  name="cuit"
                  value={form.cuit}
                  onChange={handleChange}
                />
              </Stack>
              <Stack direction="row" spacing={1.5}>
                <EditableField
                  label="Fecha de nacimiento"
                  name="birth_date"
                  value={form.birth_date}
                  onChange={handleChange}
                  type="date"
                  max={todayISO}
                  error={birthDateError}
                  helperText={birthDateError ? "La fecha no puede ser mayor a hoy" : ""}
                />
                <EditableField
                  label="Estado civil"
                  name="marital_status"
                  value={form.marital_status}
                  onChange={handleChange}
                  select
                  options={MARITAL_OPTIONS}
                />
              </Stack>
              <Stack direction="row" spacing={1.5}>
                <EditableField
                  label="Hijos"
                  name="children_count"
                  value={form.children_count}
                  onChange={handleChange}
                  type="number"
                />
                <EditableField
                  label="Nivel de educación"
                  name="education_level"
                  value={form.education_level}
                  onChange={handleChange}
                  select
                  options={EDUCATION_OPTIONS}
                />
              </Stack>
              <EditableField
                label="Salud / Obra social"
                name="health"
                value={form.health}
                onChange={handleChange}
                multiline
                rows={2}
              />
            </Stack>
          </SectionAccordion>

          {/* ─── Datos de Contacto ─── */}
          <SectionAccordion title="Datos de Contacto">
            <Stack spacing={1.5}>
              <Stack direction="row" spacing={1.5}>
                <EditableField
                  label="Nro de celular"
                  name="phone_number"
                  value={form.phone_number}
                  onChange={handleChange}
                  numericOnly
                  formatDisplay={formatPhoneNumber}
                />
                <EditableField
                  label="Nacionalidad"
                  name="nationality"
                  value={form.nationality}
                  onChange={handleChange}
                />
              </Stack>
              <Stack direction="row" spacing={1.5}>
                <EditableField
                  label="Provincia"
                  name="province"
                  value={form.province}
                  onChange={handleChange}
                />
                <EditableField
                  label="Localidad"
                  name="locality"
                  value={form.locality}
                  onChange={handleChange}
                />
              </Stack>
              <Stack direction="row" spacing={1.5}>
                <EditableField
                  label="Barrio"
                  name="neighborhood"
                  value={form.neighborhood}
                  onChange={handleChange}
                />
                <EditableField
                  label="Código postal"
                  name="postal_code"
                  value={form.postal_code}
                  onChange={handleChange}
                  numericOnly
                />
              </Stack>
              <EditableField
                label="Dirección"
                name="address"
                value={form.address}
                onChange={handleChange}
              />
            </Stack>
          </SectionAccordion>

          {/* ─── Información Laboral ─── */}
          <SectionAccordion title="Información Laboral">
            <Stack spacing={1.5}>
              <EditableField
                label="Sucursales * (Seleccione una o más opciones)"
                name="branch_ids"
                value={form.branch_ids ?? []}
                onChange={handleChange}
                select
                multiple
                placeholderMultiple="Seleccionar sucursal/es"
                options={branchOptionsMulti}
              />

              <EditableField
                label="Áreas * (Seleccione una o más opciones)"
                name="area_ids"
                value={form.area_ids ?? []}
                onChange={handleChange}
                select
                multiple
                placeholderMultiple="Seleccionar área/s"
                options={areaOptionsMulti}
              />

              <EditableField
                label="Roles * (Seleccione una o más opciones)"
                name="role_ids"
                value={form.role_ids ?? []}
                onChange={handleChange}
                select
                multiple
                placeholderMultiple="Seleccionar rol/es"
                options={roleOptionsMulti}
              />

              <Stack direction="row" spacing={1.5}>
                <EditableField
                  label="Tipo de contrato"
                  name="contract_name"
                  value={form.contract_name}
                  onChange={handleChange}
                  select
                  options={CONTRACT_TYPE_OPTIONS}
                />
                <EditableField
                  label="Fecha de ingreso"
                  name="hire_date"
                  value={form.hire_date}
                  onChange={handleChange}
                  type="date"
                />
              </Stack>

              <EditableField
                label="Indumentaria"
                name="clothing"
                value={form.clothing}
                onChange={handleChange}
                multiline
                rows={2}
              />
            </Stack>
          </SectionAccordion>

          {/* ─── Información de Contrato ─── */}
          <SectionAccordion title="Información de Contrato">
            <Stack spacing={1.5}>
              <Stack direction="row" spacing={1.5}>
                <EditableField
                  label="Estado"
                  name="status"
                  value={form.status}
                  onChange={handleChange}
                  select
                  options={STATUS_OPTIONS}
                />
              </Stack>

              <EditableField
                label="Fecha de vencimiento del contrato"
                name="contract_end_date"
                value={form.contract_end_date}
                onChange={handleChange}
                type="date"
              />

              <EditableField
                label="Modo de liquidación"
                name="salary_mode"
                value={form.salary_mode}
                onChange={handleChange}
                select
                options={SALARY_MODE_OPTIONS}
              />

              <Stack direction="row" spacing={1.5}>
                <EditableField
                  label="Sueldo fijo"
                  name="base_salary"
                  value={form.base_salary}
                  onChange={handleChange}
                  type="number"
                />
                <EditableField
                  label="Tarifa por hora"
                  name="hourly_rate"
                  value={form.hourly_rate}
                  onChange={handleChange}
                  type="number"
                />
              </Stack>

              <Typography
                sx={{
                  fontSize: "13px",
                  fontWeight: 700,
                  color: "rgba(40, 80, 160, 0.85)",
                  pt: 0.5,
                  borderTop: "1px dashed rgba(40, 80, 160, 0.2)",
                  mt: 0.5,
                }}
              >
                Datos bancarios / contables
              </Typography>
              <Stack direction="row" spacing={1.5}>
                <EditableField
                  label="Banco"
                  name="payroll_bank"
                  value={form.payroll_bank}
                  onChange={handleChange}
                />
                <EditableField
                  label="Alias de cuenta"
                  name="payroll_account_alias"
                  value={form.payroll_account_alias}
                  onChange={handleChange}
                />
              </Stack>

              <EditableField
                label="CVU"
                name="payroll_cvu"
                value={form.payroll_cvu}
                onChange={handleChange}
              />

              <EditableField
                label="Notas de liquidación"
                name="payroll_notes"
                value={form.payroll_notes}
                onChange={handleChange}
                multiline
                rows={2}
              />
            </Stack>
          </SectionAccordion>

          {/* ─── Documentos ─── */}
          <SectionAccordion title="Documentos (Proximamente)">
            <Stack spacing={1}>
              <Typography sx={{ fontSize: 13, color: "rgba(80,80,100,0.7)" }}>
                Contrato PDF:{" "}
                {isCreate ? "—" : employee?.contractPdfId ? "Disponible" : "No cargado"}
              </Typography>
              <Typography sx={{ fontSize: 13, color: "rgba(80,80,100,0.7)" }}>
                Foto de perfil:{" "}
                {isCreate ? "—" : employee?.profilePhotoId ? "Disponible" : "No cargada"}
              </Typography>
            </Stack>
          </SectionAccordion>
        </Box>

        {/* Mensaje de estado */}
        {saveMsg && (
          <Alert
            severity={saveMsgType}
            sx={{ borderRadius: "12px" }}
          >
            {saveMsg}
          </Alert>
        )}

        {/* Botones */}
        <Stack
          direction="row"
          spacing={2}
          sx={{ justifyContent: "center", flexWrap: "wrap", pt: 1 }}
        >
          {isCreate ? (
            /* ─── Create mode button ─── */
            <Button
              onClick={handleCreate}
              disabled={creating || !canCreate}
              startIcon={
                creating ? <CircularProgress size={18} /> : <SaveIcon />
              }
              variant="contained"
              sx={{
                px: 3,
                py: 1.2,
                borderRadius: "33px",
                bgcolor: canCreate
                  ? "rgba(14, 50, 149, 1)"
                  : "rgba(165, 165, 178, 0.5)",
                textTransform: "none",
                fontFamily: '"Open Sans", Helvetica, Arial, sans-serif',
                fontWeight: 700,
                fontSize: "15px",
                "&:hover": {
                  bgcolor: canCreate
                    ? "rgba(10, 40, 120, 1)"
                    : "rgba(165, 165, 178, 0.5)",
                },
              }}
            >
              {creating ? "Creando..." : "Crear empleado"}
            </Button>
          ) : (
            /* ─── Edit mode buttons ─── */
            <>
              <Button
                onClick={handleSave}
                disabled={saving || !hasChanges}
                startIcon={
                  saving ? <CircularProgress size={18} /> : <SaveIcon />
                }
                variant="contained"
                sx={{
                  px: 3,
                  py: 1.2,
                  borderRadius: "33px",
                  bgcolor: hasChanges
                    ? "rgba(14, 50, 149, 1)"
                    : "rgba(165, 165, 178, 0.5)",
                  textTransform: "none",
                  fontFamily: '"Open Sans", Helvetica, Arial, sans-serif',
                  fontWeight: 700,
                  fontSize: "15px",
                  "&:hover": {
                    bgcolor: hasChanges
                      ? "rgba(10, 40, 120, 1)"
                      : "rgba(165, 165, 178, 0.5)",
                  },
                }}
              >
                {saving ? "Guardando..." : "Guardar cambios"}
              </Button>

              <Button
                onClick={onDescargarLegajo}
                disabled={downloadingLegajo}
                sx={{
                  padding: "8px 20px",
                  backgroundColor: "rgba(216, 220, 237, 1)",
                  borderRadius: "33px",
                  textTransform: "none",
                  "&:hover": { backgroundColor: "rgba(191, 198, 225, 1)" },
                }}
              >
                <Typography
                  sx={{
                    fontFamily: '"Open Sans", Helvetica, Arial, sans-serif',
                    fontSize: "14px",
                    fontWeight: 600,
                    color: "rgba(2, 8, 23, 1)",
                  }}
                >
                  {downloadingLegajo ? "Descargando..." : "Descargar legajo"}
                </Typography>
              </Button>
            </>
          )}
        </Stack>
      </Stack>
    </Modal>
  );
}